import { RootStore } from './root.store';
import { makeAutoObservable } from 'mobx';
import { IOnboardingInformation } from '../domains/onboarding-information.domain';
import { validateAdminEmail } from '../api/signup';
import { IContact } from '../domains/common.domain';

export class OnboardingInformationStore {
  root: RootStore;
  private _information: IOnboardingInformation = {
    targetInstallDate: {
      value: null,
      errorType: 'required',
    },
    targetPortDate: { value: null },
  };
  isSameAsFirstOnboardingContact: boolean = false;

  constructor(root: RootStore) {
    makeAutoObservable(this);
    this.root = root;
  }

  init() {
    this.root.adminContactStore.init('admin');
  }

  get information(): IOnboardingInformation {
    return this._information;
  }

  addContact() {
    this.root.onboardingContactsStore.addContact();
  }

  removeContact(index: number) {
    this.root.onboardingContactsStore.removeContact(index);
  }

  updateContact(index: number, field: keyof IContact, value: string) {
    if (index === 0) this.isSameAsFirstOnboardingContact = false;
    this.root.onboardingContactsStore.updateContact(index, field, value);
  }

  updateAdminContact(field: keyof IContact, value: string) {
    this.isSameAsFirstOnboardingContact = false;
    this.root.adminContactStore.updateContact(0, field, value);
  }

  updateTargetInstallDate(date: Date | null) {
    this._information.targetInstallDate = {
      value: date,
      errorType: !date ? 'required' : undefined,
    };
    this._information.targetPortDate.errorType = this.isTargetPortDateValid(date, this._information.targetPortDate.value)
      ? undefined
      : 'invalid';
  }

  updateTargetPortDate(date: Date | null) {
    this._information.targetPortDate = {
      value: date,
      errorType: this.isTargetPortDateValid(this._information.targetInstallDate.value, date) ? undefined : 'invalid',
    };
  }

  isTargetPortDateValid(installDate: Date | null, portDate: Date | null): boolean {
    const { validatePortDate } = this.root.tenantStore.config;

    if (!validatePortDate || !installDate || !portDate) return true;

    return portDate >= installDate;
  }

  async isFormValid(): Promise<boolean> {
    const { isContactOnlyPartner, isSelfOnboarding } = this.root.quoteStore;
    const areContactsValid = isContactOnlyPartner ? true : this.root.onboardingContactsStore.isFormValid;
    const isAdminContactValid =
      !isContactOnlyPartner && isSelfOnboarding ? this.root.adminContactStore.isFormValid && !(await this.isAdminEmailInUse()) : true;
    const areDatesValid = !this._information.targetInstallDate.errorType && !this._information.targetPortDate.errorType;
    return areContactsValid && isAdminContactValid && areDatesValid;
  }

  async isAdminEmailInUse(): Promise<boolean> {
    const adminEmail = this.root.adminContactStore.contacts[0].email;
    const isEmailInUse = !(await validateAdminEmail(adminEmail.value)).isValid;
    if (isEmailInUse) adminEmail.errorType = 'in_use';
    return isEmailInUse;
  }

  updateSameAsOnboardingContact() {
    this.isSameAsFirstOnboardingContact = !this.isSameAsFirstOnboardingContact;
    if (this.isSameAsFirstOnboardingContact) {
      this.root.adminContactStore.contacts = [{ ...this.root.onboardingContactsStore.contacts[0] }];
    } else {
      this.root.adminContactStore.resetContacts();
    }
  }
}
